/**
 * Universal application-level transport for LiveKit user data.
 *
 * LiveKit's server API accepts payloads that some WebRTC subscribers cannot
 * forward (the smallest observed reliable-message limit is 65,535 bytes). A
 * successful publish therefore does not prove delivery. Every reliable
 * publisher in this repository must pass through this module, which fragments
 * oversized payloads into independently safe packets. Receivers pass every
 * packet through LiveKitDataChunkReassembler before decoding the application
 * payload.
 *
 * Chunking is the safety net, not the preferred state-sync architecture. Large
 * durable state should still travel as a compact invalidation/revision signal
 * followed by an HTTP/database hydrate so it does not head-of-line block audio
 * or other realtime control traffic.
 */

export const LIVEKIT_RELIABLE_PACKET_HARD_CAP_BYTES = 65_535;
export const LIVEKIT_DATA_WARN_BYTES = 32_000;
export const LIVEKIT_DATA_SAFE_PACKET_BYTES = 60_000;

// Base64 expands by 4/3. 42 KiB leaves several KiB for the JSON envelope and
// LiveKit framing while keeping every emitted application payload under 60 KiB.
export const LIVEKIT_DATA_RAW_CHUNK_BYTES = 42_000;
export const LIVEKIT_DATA_MAX_MESSAGE_BYTES = 4 * 1024 * 1024;
// Bound aggregate retained fragment bytes as well as message count. This keeps
// one full 4 MiB application message valid while collapsing the old worst case
// from ~127 MiB (32 nearly-complete messages) to 8 MiB per reassembler.
export const LIVEKIT_DATA_MAX_BUFFERED_BYTES = 8 * 1024 * 1024;
export const LIVEKIT_DATA_MAX_CHUNKS = Math.ceil(
  LIVEKIT_DATA_MAX_MESSAGE_BYTES / LIVEKIT_DATA_RAW_CHUNK_BYTES,
);
export const LIVEKIT_DATA_CHUNK_PROTOCOL = 'neutralaiz.livekit.chunk.v1';
const LIVEKIT_DATA_MAX_MESSAGE_ID_CHARACTERS = 128;

const DEFAULT_CHUNK_TTL_MS = 30_000;
const DEFAULT_MAX_IN_FLIGHT = 32;
const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const UTF8_ENCODER = new TextEncoder();

export class LiveKitDataMessageTooLargeError extends Error {
  readonly size: number;
  readonly maximum: number;
  readonly topic: string;

  constructor(size: number, topic: string, maximum = LIVEKIT_DATA_MAX_MESSAGE_BYTES) {
    super(
      `LiveKit data message topic="${topic}" is ${size}B; maximum supported size is ${maximum}B`,
    );
    this.name = 'LiveKitDataMessageTooLargeError';
    this.size = size;
    this.maximum = maximum;
    this.topic = topic;
  }
}

export interface LiveKitDataChunkEnvelopeV1 {
  v: typeof LIVEKIT_DATA_CHUNK_PROTOCOL;
  id: string;
  index: number;
  count: number;
  totalBytes: number;
  data: string;
}

export interface ChunkLiveKitDataOptions {
  topic?: string;
  messageId?: string;
}

export interface ReassembleLiveKitDataOptions {
  topic?: string;
  senderIdentity?: string | null;
  scope?: string;
  now?: number;
}

type PendingMessage = {
  createdAt: number;
  expiresAt: number;
  count: number;
  totalBytes: number;
  receivedBytes: number;
  receivedCount: number;
  chunks: Array<Uint8Array | undefined>;
};

type ParsedChunk =
  | { kind: 'plain' }
  | { kind: 'invalid' }
  | { kind: 'chunk'; envelope: LiveKitDataChunkEnvelopeV1; bytes: Uint8Array };

function bytesEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.byteLength !== right.byteLength) return false;
  for (let i = 0; i < left.byteLength; i += 1) {
    if (left[i] !== right[i]) return false;
  }
  return true;
}

function encodeBase64(bytes: Uint8Array): string {
  let result = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i] ?? 0;
    const hasB = i + 1 < bytes.length;
    const hasC = i + 2 < bytes.length;
    const b = hasB ? bytes[i + 1] : 0;
    const c = hasC ? bytes[i + 2] : 0;
    const packed = (a << 16) | (b << 8) | c;
    result += BASE64_ALPHABET[(packed >> 18) & 63];
    result += BASE64_ALPHABET[(packed >> 12) & 63];
    result += hasB ? BASE64_ALPHABET[(packed >> 6) & 63] : '=';
    result += hasC ? BASE64_ALPHABET[packed & 63] : '=';
  }
  return result;
}

function decodeBase64(value: string): Uint8Array | null {
  if (value.length === 0 || value.length % 4 !== 0) return null;
  const padding = value.endsWith('==') ? 2 : value.endsWith('=') ? 1 : 0;
  const outputLength = (value.length / 4) * 3 - padding;
  const output = new Uint8Array(outputLength);
  let offset = 0;

  for (let i = 0; i < value.length; i += 4) {
    const chars = [value[i], value[i + 1], value[i + 2], value[i + 3]];
    const values = chars.map((char, position) => {
      if (char === '=' && i + position >= value.length - padding) return 0;
      return BASE64_ALPHABET.indexOf(char);
    });
    if (values.some((entry) => entry < 0)) return null;
    const packed = (values[0] << 18) | (values[1] << 12) | (values[2] << 6) | values[3];
    if (offset < outputLength) output[offset++] = (packed >> 16) & 255;
    if (offset < outputLength) output[offset++] = (packed >> 8) & 255;
    if (offset < outputLength) output[offset++] = packed & 255;
  }
  return output;
}

function makeMessageId(): string {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

function messageIdCharacterCount(value: string): number {
  // Array.from counts Unicode code points, matching Python's len(str) for the
  // cross-language envelope contract rather than JavaScript UTF-16 code units.
  return Array.from(value).length;
}

function isValidMessageId(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const length = messageIdCharacterCount(value);
  return length >= 1 && length <= LIVEKIT_DATA_MAX_MESSAGE_ID_CHARACTERS;
}

function validateCustomMessageId(value: unknown): string | undefined {
  if (value === undefined) return undefined;
  if (!isValidMessageId(value)) {
    throw new RangeError('LiveKit chunk messageId must be a string containing 1..128 characters');
  }
  return value;
}

function lengthPrefixedKeyPart(value: string): string {
  return `${UTF8_ENCODER.encode(value).byteLength}:${value}`;
}

function reassemblyKey(
  scope: string,
  senderIdentity: string | null,
  topic: string,
  messageId: string,
): string {
  // A tagged nullable sender prevents RoomService packets from colliding with
  // a participant whose literal identity is "server" (or the empty string).
  const senderTag = senderIdentity === null ? '0' : '1';
  return [scope, senderTag, senderIdentity ?? '', topic, messageId]
    .map(lengthPrefixedKeyPart)
    .join('');
}

function validateChunkEnvelope(raw: unknown): LiveKitDataChunkEnvelopeV1 | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  if (value.v !== LIVEKIT_DATA_CHUNK_PROTOCOL) return null;
  if (
    !isValidMessageId(value.id) ||
    !Number.isInteger(value.index) ||
    !Number.isInteger(value.count) ||
    !Number.isInteger(value.totalBytes) ||
    typeof value.data !== 'string'
  ) {
    return null;
  }
  const index = value.index as number;
  const count = value.count as number;
  const totalBytes = value.totalBytes as number;
  if (
    count < 2 ||
    count > LIVEKIT_DATA_MAX_CHUNKS ||
    index < 0 ||
    index >= count ||
    totalBytes <= LIVEKIT_DATA_SAFE_PACKET_BYTES ||
    totalBytes > LIVEKIT_DATA_MAX_MESSAGE_BYTES
  ) {
    return null;
  }
  return value as unknown as LiveKitDataChunkEnvelopeV1;
}

function parseChunkPacket(payload: Uint8Array): ParsedChunk {
  // Every chunk envelope is JSON. Avoid decoding/parsing ordinary binary data.
  if (payload.byteLength === 0 || payload[0] !== 0x7b) return { kind: 'plain' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(payload));
  } catch {
    return { kind: 'plain' };
  }
  if (
    !parsed ||
    typeof parsed !== 'object' ||
    (parsed as Record<string, unknown>).v !== LIVEKIT_DATA_CHUNK_PROTOCOL
  ) {
    return { kind: 'plain' };
  }
  const envelope = validateChunkEnvelope(parsed);
  if (!envelope) return { kind: 'invalid' };
  const bytes = decodeBase64(envelope.data);
  if (!bytes || bytes.byteLength === 0 || bytes.byteLength > LIVEKIT_DATA_RAW_CHUNK_BYTES) {
    return { kind: 'invalid' };
  }
  return { kind: 'chunk', envelope, bytes };
}

export function assertLiveKitDataPacketSafe(payload: Uint8Array, topic = ''): void {
  if (payload.byteLength > LIVEKIT_DATA_SAFE_PACKET_BYTES) {
    throw new LiveKitDataMessageTooLargeError(
      payload.byteLength,
      topic,
      LIVEKIT_DATA_SAFE_PACKET_BYTES,
    );
  }
}

/**
 * Return one packet for a safe payload, or a sequence of safe chunk envelopes
 * for an oversized reliable payload. Every returned packet is <= 60 KiB.
 */
export function chunkLiveKitDataPayload(
  payload: Uint8Array,
  options: ChunkLiveKitDataOptions = {},
): Uint8Array[] {
  const customMessageId = validateCustomMessageId(options.messageId);
  if (payload.byteLength <= LIVEKIT_DATA_SAFE_PACKET_BYTES) return [payload];
  if (payload.byteLength > LIVEKIT_DATA_MAX_MESSAGE_BYTES) {
    throw new LiveKitDataMessageTooLargeError(payload.byteLength, options.topic ?? '');
  }

  const count = Math.ceil(payload.byteLength / LIVEKIT_DATA_RAW_CHUNK_BYTES);
  if (count > LIVEKIT_DATA_MAX_CHUNKS) {
    throw new LiveKitDataMessageTooLargeError(payload.byteLength, options.topic ?? '');
  }
  const messageId = customMessageId ?? makeMessageId();
  const packets: Uint8Array[] = [];

  for (let index = 0; index < count; index += 1) {
    const start = index * LIVEKIT_DATA_RAW_CHUNK_BYTES;
    const end = Math.min(start + LIVEKIT_DATA_RAW_CHUNK_BYTES, payload.byteLength);
    const envelope: LiveKitDataChunkEnvelopeV1 = {
      v: LIVEKIT_DATA_CHUNK_PROTOCOL,
      id: messageId,
      index,
      count,
      totalBytes: payload.byteLength,
      data: encodeBase64(payload.subarray(start, end)),
    };
    const packet = UTF8_ENCODER.encode(JSON.stringify(envelope));
    assertLiveKitDataPacketSafe(packet, options.topic);
    packets.push(packet);
  }
  return packets;
}

/**
 * Bounded receiver for the chunk protocol. `accept` returns the original
 * payload immediately for an ordinary packet, the reassembled payload after
 * the final chunk, and null for an incomplete or invalid chunk envelope.
 */
export class LiveKitDataChunkReassembler {
  private readonly pending = new Map<string, PendingMessage>();
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private bufferedBytes = 0;

  constructor(
    private readonly ttlMs = DEFAULT_CHUNK_TTL_MS,
    private readonly maxInFlight = DEFAULT_MAX_IN_FLIGHT,
    private readonly maxBufferedBytes = LIVEKIT_DATA_MAX_BUFFERED_BYTES,
  ) {
    if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
      throw new RangeError('LiveKit chunk TTL must be a positive finite number');
    }
    if (!Number.isInteger(maxInFlight) || maxInFlight <= 0) {
      throw new RangeError('LiveKit chunk in-flight limit must be a positive integer');
    }
    if (!Number.isInteger(maxBufferedBytes) || maxBufferedBytes < LIVEKIT_DATA_RAW_CHUNK_BYTES) {
      throw new RangeError(
        `LiveKit chunk buffer limit must be an integer >= ${LIVEKIT_DATA_RAW_CHUNK_BYTES}`,
      );
    }
  }

  clear(): void {
    this.cancelExpiryTimer();
    this.pending.clear();
    this.bufferedBytes = 0;
  }

  get inFlightCount(): number {
    return this.pending.size;
  }

  get bufferedByteCount(): number {
    return this.bufferedBytes;
  }

  accept(payload: Uint8Array, options: ReassembleLiveKitDataOptions = {}): Uint8Array | null {
    const usesWallClock = options.now === undefined;
    if (options.now !== undefined && !Number.isFinite(options.now)) {
      throw new RangeError('LiveKit chunk reassembly clock must be a finite number');
    }
    const now = options.now ?? Date.now();
    // Sweep before classifying the current packet as well as on the autonomous
    // timer. Ordinary application traffic must not leave expired fragments
    // retained just because it is not itself a chunk envelope.
    this.expire(now);
    const parsed = parseChunkPacket(payload);
    if (parsed.kind === 'plain') return payload;
    if (parsed.kind === 'invalid') return null;

    const { envelope, bytes } = parsed;
    const key = reassemblyKey(
      options.scope ?? '',
      options.senderIdentity ?? null,
      options.topic ?? '',
      envelope.id,
    );

    let entry = this.pending.get(key);
    if (!entry) {
      if (this.pending.size >= this.maxInFlight) this.evictOldest();
      entry = {
        createdAt: now,
        expiresAt: now + this.ttlMs,
        count: envelope.count,
        totalBytes: envelope.totalBytes,
        receivedBytes: 0,
        receivedCount: 0,
        chunks: new Array<Uint8Array | undefined>(envelope.count),
      };
      this.pending.set(key, entry);
    } else if (entry.count !== envelope.count || entry.totalBytes !== envelope.totalBytes) {
      this.deletePending(key);
      return null;
    }
    // A test/manual-clock first chunk may later join real wall-clock traffic.
    // Ensure that transition gains autonomous expiry rather than retaining the
    // original timer-less partial indefinitely.
    if (usesWallClock) this.scheduleExpiryTimer();

    const existing = entry.chunks[envelope.index];
    if (existing) {
      if (!bytesEqual(existing, bytes)) this.deletePending(key);
      return null;
    }

    if (!this.ensureBufferedCapacity(bytes.byteLength, key)) {
      this.deletePending(key);
      return null;
    }
    entry.chunks[envelope.index] = bytes;
    entry.receivedBytes += bytes.byteLength;
    entry.receivedCount += 1;
    this.bufferedBytes += bytes.byteLength;
    if (entry.receivedBytes > entry.totalBytes) {
      this.deletePending(key);
      return null;
    }
    if (entry.receivedCount !== entry.count) return null;
    if (entry.receivedBytes !== entry.totalBytes) {
      this.deletePending(key);
      return null;
    }

    const complete = new Uint8Array(entry.totalBytes);
    let offset = 0;
    for (const chunk of entry.chunks) {
      if (!chunk) {
        this.deletePending(key);
        return null;
      }
      complete.set(chunk, offset);
      offset += chunk.byteLength;
    }
    this.deletePending(key);
    return complete;
  }

  private expire(now: number): void {
    for (const [key, entry] of this.pending) {
      if (entry.expiresAt <= now) this.deletePending(key);
    }
    if (this.pending.size === 0) this.cancelExpiryTimer();
  }

  private evictOldest(): void {
    let oldestKey: string | null = null;
    let oldestCreatedAt = Number.POSITIVE_INFINITY;
    for (const [key, entry] of this.pending) {
      if (entry.createdAt < oldestCreatedAt) {
        oldestCreatedAt = entry.createdAt;
        oldestKey = key;
      }
    }
    if (oldestKey !== null) this.deletePending(oldestKey);
  }

  private deletePending(key: string): void {
    const entry = this.pending.get(key);
    if (!entry) return;
    this.pending.delete(key);
    this.bufferedBytes -= entry.receivedBytes;
    if (this.bufferedBytes < 0) {
      throw new Error('LiveKit chunk buffered-byte accounting underflow');
    }
    if (this.pending.size === 0) this.cancelExpiryTimer();
  }

  private ensureBufferedCapacity(additionalBytes: number, protectedKey: string): boolean {
    while (this.bufferedBytes + additionalBytes > this.maxBufferedBytes) {
      let oldestKey: string | null = null;
      let oldestCreatedAt = Number.POSITIVE_INFINITY;
      for (const [key, entry] of this.pending) {
        if (key !== protectedKey && entry.createdAt < oldestCreatedAt) {
          oldestCreatedAt = entry.createdAt;
          oldestKey = key;
        }
      }
      if (oldestKey === null) return false;
      this.deletePending(oldestKey);
    }
    return true;
  }

  private scheduleExpiryTimer(): void {
    if (this.expiryTimer !== null || this.pending.size === 0) return;
    let earliestExpiry = Number.POSITIVE_INFINITY;
    for (const entry of this.pending.values()) {
      earliestExpiry = Math.min(earliestExpiry, entry.expiresAt);
    }
    const delay = Math.max(0, earliestExpiry - Date.now());
    const handle = setTimeout(() => {
      this.expiryTimer = null;
      this.expire(Date.now());
      this.scheduleExpiryTimer();
    }, delay);
    // A partial message must not keep a Node test/SSR process alive. Browser
    // timeout handles are numbers and simply do not expose unref().
    const unref = (handle as unknown as { unref?: () => void }).unref;
    if (typeof unref === 'function') unref.call(handle);
    this.expiryTimer = handle;
  }

  private cancelExpiryTimer(): void {
    if (this.expiryTimer === null) return;
    clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
  }
}
